/**
 * One candidate rule of the draft pack, side by side with its source passage.
 *
 * The draft carries NO comparison — not an empty one, not a guessed one, the
 * field is simply not there. That is the M4 contract: an auto-generated pack is
 * data only, and a rule becomes computable when a human reads the cited article
 * and types the operator and the limit in. So this card's form is not "review
 * the extracted formula", it is "write the comparison down from the source" —
 * and the commit stays blocked until both parts are there.
 *
 * `verwerfen` is a peer of `stimmt`, not a hidden option: the extraction will
 * sometimes turn a sentence that is not a rule into a candidate, and discarding
 * it must be as cheap as accepting it.
 *
 * `readOnly` (every non-founder session, Founder decision 2026-09-16): the
 * candidate and its passage stay readable, the form and the commit are not
 * rendered, and the commit column says who verifies. The backend answers 403
 * to the same session either way.
 */
import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'

import { api, type RuleVerifyInput } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/i18n'

import { SourcePanel } from './SourcePanel'
import { groupCitations, isCheckable } from './rulebase'
import type { CheckOp, Citation, DraftPack, DraftRule } from '@/api/types'

type RuleDisposition = 'confirmed' | 'corrected' | 'rejected'

const OPS: CheckOp[] = ['<=', '>=', '==', '<', '>']
const KINDS = ['computable', 'required', 'nachweis', 'narrative'] as const

export function RuleCard({
  workspaceId,
  packId,
  rule,
  onUpdated,
  readOnly = false,
}: {
  workspaceId: string
  packId: string
  rule: DraftRule
  onUpdated: (pack: DraftPack) => void
  readOnly?: boolean
}) {
  const { t } = useI18n()
  const citation = (rule as { citation?: Citation | null }).citation ?? null
  const checkable = isCheckable(citation)

  const [disposition, setDisposition] = useState<RuleDisposition | null>(null)
  const [kind, setKind] = useState<string>(rule.kind ?? '')
  const [op, setOp] = useState<CheckOp>('<=')
  const [limit, setLimit] = useState('')
  const [artifact, setArtifact] = useState(rule.artifact ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const verified = rule.status === 'verified'
  const needsCheck =
    disposition !== 'rejected' && kind === 'computable' && limit.trim() === ''
  const ready = disposition !== null && !needsCheck && !busy

  const submit = async () => {
    if (!ready || !citation) return
    setBusy(true)
    setError(null)
    try {
      const input: RuleVerifyInput = {
        disposition,
        citation,
        ...(disposition === 'rejected'
          ? {}
          : {
              kind: kind || null,
              ...(kind === 'computable'
                ? { check: { op, value: Number(limit.trim().replace(/'/g, '')) } }
                : {}),
              ...(kind === 'required' && artifact.trim() ? { artifact: artifact.trim() } : {}),
            }),
      }
      const pack = await api.verifyRule(workspaceId, packId, rule.id, input)
      setSaved(true)
      setDisposition(null)
      onUpdated(pack)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <li>
      <div
        className={[
          'rounded-md border bg-white px-6 py-5',
          verified ? 'border-border' : 'border-dashed border-border-strong',
        ].join(' ')}
        data-testid="rule-card"
        data-rule={rule.id}
        data-status={rule.status}
        data-readonly={readOnly}
      >
        {/* An h3, matching the zone rows: the h4 headings inside the columns
            below must not jump two levels from the section's h2. */}
        <h3 className="m-0 flex flex-wrap items-center gap-3 text-[15px] font-semibold">
          <code className="num text-[13px] font-normal text-muted-2">{rule.id}</code>
          <span className="text-ink">{rule.title_de}</span>
          <Badge variant={verified ? 'info' : 'indicative'}>
            {verified ? <Icon icon={ShieldCheck} /> : null}
            {t(`verify.status.${rule.status}`)}
          </Badge>
        </h3>

        <div className="mt-4 grid gap-8 md:grid-cols-2">
          {/* LEFT — what a human has to write down from the source. */}
          <div className="flex flex-col gap-4">
            <h4 className="text-[13px] font-semibold text-ink-2">{t('verify.ruleCheckTitle')}</h4>
            <p className="text-[12px] text-muted" data-testid="rule-no-check">
              {t('verify.ruleNoCheck')}
            </p>

            {!checkable ? (
              <p className="text-[12px] text-muted" data-testid="no-citation">
                {t('verify.noCitation')}
              </p>
            ) : readOnly ? null : (
              <>
                <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <legend className="text-[13px] font-medium text-ink-2">
                    {t('verify.disposition')}
                  </legend>
                  {(['confirmed', 'corrected', 'rejected'] as RuleDisposition[]).map((value) => (
                    <label key={value} className="flex items-center gap-1.5 text-[13px] text-ink">
                      <input
                        type="radio"
                        name={`rule-${rule.id}-disposition`}
                        value={value}
                        checked={disposition === value}
                        onChange={() => {
                          setSaved(false)
                          setDisposition(value)
                        }}
                        className="accent-accent"
                      />
                      {value === 'confirmed'
                        ? t('verify.confirm')
                        : value === 'corrected'
                          ? t('verify.correct')
                          : t('verify.ruleReject')}
                    </label>
                  ))}
                </fieldset>
                <p className="text-[12px] text-muted">{t('verify.ruleRejectHint')}</p>

                {disposition !== 'rejected' ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor={`rule-${rule.id}-kind`}
                        className="text-[13px] font-medium text-ink-2"
                      >
                        {t('verify.ruleKind')}
                      </label>
                      <select
                        id={`rule-${rule.id}-kind`}
                        value={kind}
                        onChange={(event) => setKind(event.target.value)}
                        className="h-9 w-56 rounded-sm border border-border-strong bg-white px-2 text-[13px] text-ink"
                      >
                        <option value="">{t('verify.ruleKindUnknown')}</option>
                        {KINDS.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>

                    {kind === 'computable' ? (
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1.5">
                          <label
                            htmlFor={`rule-${rule.id}-op`}
                            className="text-[13px] font-medium text-ink-2"
                          >
                            {t('verify.ruleOp')}
                          </label>
                          <select
                            id={`rule-${rule.id}-op`}
                            value={op}
                            onChange={(event) => setOp(event.target.value as CheckOp)}
                            className="num h-9 w-20 rounded-sm border border-border-strong bg-white px-2 text-[13px] text-ink"
                          >
                            {OPS.map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label
                            htmlFor={`rule-${rule.id}-limit`}
                            className="text-[13px] font-medium text-ink-2"
                          >
                            {t('verify.ruleValue')}
                          </label>
                          <input
                            id={`rule-${rule.id}-limit`}
                            type="text"
                            inputMode="decimal"
                            value={limit}
                            onChange={(event) => setLimit(event.target.value)}
                            placeholder={t('verify.ruleValuePlaceholder')}
                            className="num h-9 w-40 rounded-sm border border-border-strong bg-white px-2 text-[13px] text-ink placeholder:text-muted-2"
                          />
                        </div>
                      </div>
                    ) : null}

                    {kind === 'required' ? (
                      <div className="flex flex-col gap-1.5">
                        <label
                          htmlFor={`rule-${rule.id}-artifact`}
                          className="text-[13px] font-medium text-ink-2"
                        >
                          {t('verify.ruleArtifact')}
                        </label>
                        <input
                          id={`rule-${rule.id}-artifact`}
                          type="text"
                          value={artifact}
                          onChange={(event) => setArtifact(event.target.value)}
                          className="h-9 w-full rounded-sm border border-border-strong bg-white px-2 text-[13px] text-ink"
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>

          {/* RIGHT — the passage, and the commit that records against it. */}
          <div className="flex flex-col gap-4">
            <h4 className="text-[13px] font-semibold text-ink-2">{t('verify.sourceTitle')}</h4>
            {/* One rule, one passage — naming what it covers would just repeat
                the title standing above it. */}
            <SourcePanel groups={groupCitations([{ label: '', citation }])} />

            {checkable && readOnly ? (
              <p
                className="border-t border-border pt-4 text-[12px] text-muted"
                data-testid="rule-founder-only"
              >
                {t('verify.founderOnlyRow')}
              </p>
            ) : checkable ? (
              <div className="flex flex-col gap-3 border-t border-border pt-4">
                {needsCheck ? (
                  <p role="alert" className="text-[12px] text-signal-red-text">
                    {t('verify.ruleNeedsCheck')}
                  </p>
                ) : null}
                <Button
                  className="self-start"
                  disabled={!ready}
                  onClick={() => void submit()}
                  data-testid="commit-rule"
                >
                  {busy ? t('verify.submitting') : t('verify.ruleSubmit')}
                </Button>
                {saved ? (
                  <p role="status" className="text-[12px] text-muted">
                    {t('verify.rowSaved')}
                  </p>
                ) : null}
                {error ? <ErrorNote message={`${t('verify.rowError')}: ${error}`} /> : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  )
}
